import { useCallback, useEffect, useRef, useState } from "react"
import "../Stylesheets/Header/Header.css"
import { Modal } from 'react-bootstrap'
import { Bell, List, PersonCircle, Lock, BoxArrowLeft } from "react-bootstrap-icons"
import OutsideClickHandler from 'react-outside-click-handler';
import { useSelector } from "react-redux";
import api, { isCanceled } from "../lib/api";
import useNotify from "../hooks/useNotify";
import ChangePasswordModal from "../Components/ChangePasswordModal";

const POLL_INTERVAL = 1000 * 120;

const Header = ({ setShowFullWidth, setNotification }) => {
    const [notifications, setNotifications] = useState([]);
    const [showProfileDropdown, setShowProfileDropdown] = useState(false);
    const [modal, setModal] = useState({ show: false, data: {} });
    const [showNotification, setShowNotification] = useState(false);
    const [reads, setReads] = useState([]);
    const [changePassword, setChangePassword] = useState({ show: false, password: "", repeat_password: "", loader: false });
    const notify = useNotify(setNotification);
    const user = useSelector((state) => state.user);

    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const loadNotifications = useCallback(async () => {
        try {
            const [listRes, readsRes] = await Promise.all([
                api.get('/notifications'),
                api.get('/read-notifications'),
            ]);
            if (!mountedRef.current) return;
            setNotifications(Array.isArray(listRes.data.data) ? listRes.data.data : []);
            setReads((readsRes.data.data || []).map(String));
        } catch (err) {
            if (isCanceled(err) || !mountedRef.current) return;
            setNotifications([]);
        }
    }, []);

    useEffect(() => {
        loadNotifications();
        const id = setInterval(loadNotifications, POLL_INTERVAL);
        // Cleared on unmount. Without this the interval outlived logout and
        // kept polling forever, and each re-mount stacked another one.
        return () => clearInterval(id);
    }, [loadNotifications]);

    // Derived from the ids actually on screen, so it cannot go negative the way
    // `notifications.length - reads.length` could when `reads` held all-time
    // reads from notifications no longer in the list.
    const unreadCount = notifications.filter((n) => !reads.includes(String(n.id))).length;

    const handlers = {
        logout() {
            api.post('/logout')
                .then(() => { window.location.href = "/"; })
                .catch((err) => notify.error(err, "Could not log out."));
        },
        openNotification(notification) {
            setModal({ show: true, data: { title: notification.title, html: notification.html, color: notification.alert_type } });
            setShowNotification(false);

            const id = String(notification.id);
            if (reads.includes(id)) return;

            // Marked read locally first so the badge responds immediately.
            setReads((prev) => [...prev, id]);
            api.post(`/notification/${notification.id}/is-read`)
                .catch((err) => {
                    if (isCanceled(err)) return;
                    setReads((prev) => prev.filter((value) => value !== id));
                });
        },
        changePasswordHandler(e) {
            e.preventDefault();
            if (changePassword.password !== changePassword.repeat_password) {
                notify.error(null, "Passwords don't match");
                return;
            }
            setChangePassword((prev) => ({ ...prev, loader: true }));
            api.put('/change-user-password', {
                currentPassword: changePassword.current_password,
                newPassword: changePassword.password,
                confirmPassword: changePassword.repeat_password,
            })
                .then((res) => {
                    notify.success(res.data.message.en);
                    setChangePassword({ show: false, password: "", repeat_password: "", loader: false });
                })
                .catch((err) => {
                    notify.error(err);
                    setChangePassword((prev) => ({ ...prev, loader: false }));
                });
        },
    };

    const initials = `${(user.name || user.username || "?").charAt(0)}${(user.surname || "").charAt(0)}`.toUpperCase();

    return (
        <>
            <header className="header-cotnainer">
                <div className="header-left-side-items">
                    <button
                        type="button"
                        className="header-menu-button"
                        aria-label="Toggle navigation"
                        onClick={() => setShowFullWidth((prev) => !prev)}
                    ><List className="header-menu-icon" /></button>
                </div>
                <div className="header-right-side">
                    <div className="notifications-container">
                        <button
                            className="notification-button"
                            aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications"}
                            onClick={() => setShowNotification((prev) => !prev)}
                        ><Bell />{unreadCount > 0 && <span className="notification-dot"></span>}</button>
                        {showNotification && <OutsideClickHandler onOutsideClick={() => setShowNotification(false)}>
                            <div className="noftications-box">
                                <h6 className="notifications-box-title">Notifications</h6>
                                {notifications.length === 0 && <span className="empty-notifications">No Notifications</span>}
                                {notifications.map((notification) => (
                                    // Keyed by id, not array index: the list is
                                    // refetched on a timer and rows shift.
                                    <div
                                        className={`notification-item ${reads.includes(String(notification.id)) ? "" : "unread-notifications"}`}
                                        key={notification.id}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => handlers.openNotification(notification)}
                                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handlers.openNotification(notification); }}
                                    >
                                        <span className="notification-item-title" style={{ color: notification.alert_type }}>{notification.title}</span>
                                        <span className="notification-item-body">{notification.html}</span>
                                    </div>
                                ))}
                            </div>
                        </OutsideClickHandler>}
                    </div>
                    <div className="profile-dropdown-button">
                        <button
                            className="profile-dropdown-btn"
                            aria-label="Account menu"
                            onClick={() => setShowProfileDropdown((prev) => !prev)}
                        >{initials.trim() ? <span className="profile-initials">{initials}</span> : <PersonCircle />}</button>
                        {showProfileDropdown && <OutsideClickHandler onOutsideClick={() => setShowProfileDropdown(false)}>
                            <div className="profile-dropdown-box">
                                <div className="profile-dropdown-identity">
                                    <span className="profile-dropdown-name">{user.name} {user.surname}</span>
                                    <span className="profile-dropdown-role">{user.role}</span>
                                </div>
                                <ul>
                                    {/* "Account Settings" pointed at /account-settings,
                                        which has no route and no page — it blanked the
                                        content area. Removed until that page exists.
                                        "Change Password" was a dead <li> with no
                                        handler; it now opens the working modal. */}
                                    <li>
                                        <button type="button" onClick={() => { setShowProfileDropdown(false); setChangePassword({ show: true, current_password: "", password: "", repeat_password: "", loader: false }); }}>
                                            <Lock /><span>Change Password</span>
                                        </button>
                                    </li>
                                </ul>
                                <ul>
                                    <li>
                                        <button type="button" onClick={handlers.logout}><BoxArrowLeft /><span>Log Out</span></button>
                                    </li>
                                </ul>
                            </div>
                        </OutsideClickHandler>}
                    </div>
                </div>
            </header>
            <Modal show={modal.show} onHide={() => setModal((prev) => ({ ...prev, show: false }))} size="md" centered>
                <Modal.Header closeButton>
                    <div className="notification-modal-heading">
                        <span style={{ color: modal.data.color }}>{modal.data.title}</span>
                        <h4 style={{ color: modal.data.color }}>
                            {modal.data.color === "#28a745" && "Success"}
                            {modal.data.color === "#17a2b8" && "Info"}
                            {modal.data.color === "#ffc107" && "Warning"}
                            {modal.data.color === "#dc3545" && "Problem"}
                        </h4>
                    </div>
                </Modal.Header>
                <Modal.Body><span style={{ color: modal.data.color }}>{modal.data.html}</span></Modal.Body>
                <Modal.Footer>
                    <button className="submit-modal-form-btn" style={{ width: "100px" }} onClick={() => setModal((prev) => ({ ...prev, show: false }))}>Ok</button>
                </Modal.Footer>
            </Modal>
            <ChangePasswordModal
                changePassword={changePassword}
                setChangePassword={setChangePassword}
                onSubmit={handlers.changePasswordHandler}
                requireCurrent
            />
        </>
    )
}

export default Header
